import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Table, Select, DatePicker, Input, Button, Modal, message, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  g203Api,
  ARReplyItem,
  SaveItem,
  PaymentDetail,
  ReplyCodeOption,
} from '../../services/g203Api';
import { BatchAcceptanceBridge, pwg203f1AcceptanceManifest } from '../../features/acceptance';
import './G203Form.css';

const { TextArea } = Input;

// ── Helper ──────────────────────────────────────────────────
const fmtAmt = (v: number | null | undefined): string =>
  v == null || v === 0 ? '0.00' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (v: string | null | undefined): string =>
  v ? v.substring(0, 10) : '';

const rowKey = (r: ARReplyItem): string => `${r.customerId}-${r.deliveryNo}`;

// Codes that are system-auto-set (readonly reply)
const AUTO_SET_CODES = new Set(['09', '10']);
// Codes that disable recovery date
const NO_RECOVERY_CODES = new Set(['09', '10', '11']);

// ── Component ───────────────────────────────────────────────
const G203Form: React.FC = () => {
  // Filter state
  const [customerId, setCustomerId] = useState('');
  const [salesPersonId, setSalesPersonId] = useState('');

  // Data state
  const [items, setItems] = useState<ARReplyItem[]>([]);
  const [originalItems, setOriginalItems] = useState<ARReplyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replyCodeOptions, setReplyCodeOptions] = useState<ReplyCodeOption[]>([]);
  const [acceptanceLastApiResponse, setAcceptanceLastApiResponse] = useState<unknown>(null);

  // Selection
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);

  // Payment detail modal
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetail[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const selectedItem = useMemo(
    () => items.find((i) => rowKey(i) === selectedRowKey) ?? null,
    [items, selectedRowKey],
  );

  // ── Load reply code options on mount ──
  useEffect(() => {
    g203Api.getReplyCodeOptions().then((res) => {
      setReplyCodeOptions(res.data);
      setAcceptanceLastApiResponse(res.data);
    }).catch(() => {
      message.error('載入回覆代碼失敗');
    });
  }, []);

  // ── Query handler ──
  const handleQuery = useCallback(async () => {
    if (!customerId.trim() && !salesPersonId.trim()) {
      message.warning('下單客戶與業務代號不可以同時為空!');
      return;
    }
    setLoading(true);
    try {
      const res = await g203Api.query(customerId.trim(), salesPersonId.trim());
      const data = res.data;
      setAcceptanceLastApiResponse(data);
      setItems(data.items || []);
      setOriginalItems(data.items || []);
      setSelectedRowKey(null);
      if (!data.items || data.items.length === 0) {
        message.info('查無資料');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      message.error(msg || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [customerId, salesPersonId]);

  // ── Inline edit helpers ──
  const updateItem = useCallback((key: string, field: keyof ARReplyItem, value: unknown) => {
    setItems((prev) =>
      prev.map((item) => {
        if (rowKey(item) !== key) return item;
        const updated = { ...item, [field]: value };
        // When reply code changes to 09/10/11, clear recovery date
        if (field === 'replyCode' && NO_RECOVERY_CODES.has(value as string)) {
          updated.expectedRecoveryDate = null;
        }
        // When reply code is NOT 12, clear other factors
        if (field === 'replyCode' && (value as string) !== '12') {
          updated.otherFactors = '';
        }
        return updated;
      }),
    );
  }, []);

  // ── Save handler ──
  const handleSave = useCallback(async () => {
    // Collect changed items
    const changedItems: SaveItem[] = [];
    for (const item of items) {
      const orig = originalItems.find(
        (o) => o.customerId === item.customerId && o.deliveryNo === item.deliveryNo,
      );
      if (
        !orig ||
        orig.replyCode !== item.replyCode ||
        orig.expectedRecoveryDate !== item.expectedRecoveryDate ||
        orig.expectedWriteOffDate !== item.expectedWriteOffDate ||
        orig.otherFactors !== item.otherFactors
      ) {
        changedItems.push({
          customerId: item.customerId,
          deliveryNo: item.deliveryNo,
          replyCode: item.replyCode,
          expectedRecoveryDate: item.expectedRecoveryDate,
          expectedWriteOffDate: item.expectedWriteOffDate,
          otherFactors: item.otherFactors,
        });
      }
    }
    if (changedItems.length === 0) {
      message.info('沒有修改的資料');
      return;
    }
    setSaving(true);
    try {
      const res = await g203Api.save(changedItems);
      const data = res.data;
      setAcceptanceLastApiResponse(data);
      if (data.errors && data.errors.length > 0) {
        message.error(data.errors.join('\n'));
      } else {
        message.success(`存檔完成，更新 ${data.updatedCount} 筆`);
        // Clear table after successful save
        setItems([]);
        setOriginalItems([]);
        setSelectedRowKey(null);
      }
    } catch (err: unknown) {
      const errData = (err as { response?: { data?: { error?: string; errors?: string[] } } })
        ?.response?.data;
      if (errData?.errors && errData.errors.length > 0) {
        message.error(errData.errors.join('\n'));
      } else {
        message.error(errData?.error || '存檔失敗');
      }
    } finally {
      setSaving(false);
    }
  }, [items, originalItems]);

  // ── Payment detail modal ──
  const handleShowPaymentDetail = useCallback(async () => {
    if (!selectedItem || !selectedItem.hasPaymentDetails) return;
    setDetailLoading(true);
    setDetailModalVisible(true);
    try {
      const res = await g203Api.getPaymentDetails(
        selectedItem.customerId,
        selectedItem.deliveryNo,
      );
      setPaymentDetails(res.data || []);
      setAcceptanceLastApiResponse(res.data);
    } catch {
      message.error('載入收款明細失敗');
      setPaymentDetails([]);
    } finally {
      setDetailLoading(false);
    }
  }, [selectedItem]);

  // ── Reply code description for selected row ──
  const replyDesc = useMemo(() => {
    if (!selectedItem || !selectedItem.replyCode) return '';
    return replyCodeOptions.find((o) => o.code === selectedItem.replyCode)?.label ?? '';
  }, [selectedItem, replyCodeOptions]);

  // ── Table columns ──
  const columns: ColumnsType<ARReplyItem> = useMemo(
    () => [
      { title: <span data-contract-field="ui_row_num">項數</span>, dataIndex: 'rowNum', width: 60, align: 'center' },
      { title: '客戶代號', dataIndex: 'customerId', width: 120 },
      { title: '交運單號', dataIndex: 'deliveryNo', width: 110 },
      {
        title: <span data-contract-field="fg03_22">交運日期</span>, dataIndex: 'deliveryDate', width: 100, align: 'center',
        render: (v: string | null) => fmtDate(v),
      },
      {
        title: <span data-contract-field="fg03_54">業務回覆</span>, dataIndex: 'replyCode', width: 200,
        render: (_: string, record: ARReplyItem) => (
          <Select
            className="reply-code-select"
            size="small"
            style={{ width: '100%' }}
            value={record.replyCode || undefined}
            onChange={(val) => updateItem(rowKey(record), 'replyCode', val)}
            disabled={record.isAutoSet}
            allowClear
            placeholder="選擇回覆"
          >
            {replyCodeOptions.map((opt) => (
              <Select.Option
                key={opt.code}
                value={opt.code}
                disabled={AUTO_SET_CODES.has(opt.code)}
              >
                {opt.code} - {opt.label}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        title: <span data-contract-field="fg03_55">預計收回日期</span>, dataIndex: 'expectedRecoveryDate', width: 140, align: 'center',
        render: (_: unknown, record: ARReplyItem) => {
          const disabled = record.isAutoSet || NO_RECOVERY_CODES.has(record.replyCode);
          return (
            <DatePicker
              className="recovery-date-picker"
              size="small"
              value={record.expectedRecoveryDate ? dayjs(record.expectedRecoveryDate) : null}
              onChange={(d) =>
                updateItem(
                  rowKey(record),
                  'expectedRecoveryDate',
                  d ? d.format('YYYY-MM-DDTHH:mm:ssZ') : null,
                )
              }
              disabled={disabled}
              style={{ width: '100%' }}
            />
          );
        },
      },
    ],
    [replyCodeOptions, updateItem],
  );

  // ── Payment detail columns ──
  const detailColumns: ColumnsType<PaymentDetail> = [
    { title: '項次', dataIndex: 'rowNum', width: 60, align: 'center' },
    { title: '沖帳單號', dataIndex: 'writeOffDocNo', width: 140 },
    { title: '單據類別', dataIndex: 'docTypeText', width: 120 },
    {
      title: '沖帳金額', dataIndex: 'writeOffAmt', width: 130, align: 'right',
      render: (v: number) => fmtAmt(v),
    },
    {
      title: '沖帳日期', dataIndex: 'writeOffDate', width: 110, align: 'center',
      render: (v: string | null) => fmtDate(v),
    },
  ];
  const queryAcceptanceExact = useCallback(async (key: string) => {
    const [customer, delivery] = key.split('|');
    const response = await g203Api.query(customer || '', '');
    return response.data.items.find(item => item.deliveryNo === delivery) || null;
  }, []);

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="g203-form">
      <h2 className="g203-title">應收帳款回覆作業</h2>

      {/* Filter bar */}
      <div className="g203-filter-bar" data-acceptance-action="PWG203-ACT-GET-REPLY-CODE-OPTIONS">
        <label data-contract-field="query_customer_id">下單客戶</label>
        <Input
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          placeholder="客戶代號"
          maxLength={12}
          onPressEnter={handleQuery}
        />
        <label data-contract-field="query_salesperson_id">業務代號</label>
        <Input
          value={salesPersonId}
          onChange={(e) => setSalesPersonId(e.target.value)}
          placeholder="業務代號"
          maxLength={8}
          onPressEnter={handleQuery}
        />
        <Button type="primary" data-acceptance-action="PWG203-ACT-QUERY" onClick={handleQuery} loading={loading}>
          確定範圍
        </Button>
      </div>

      {/* Main table */}
      <Spin spinning={loading}>
        <Table<ARReplyItem>
          columns={columns}
          dataSource={items}
          rowKey={rowKey}
          size="small"
          pagination={false}
          scroll={{ y: 320 }}
          rowClassName={(record) => {
            const classes: string[] = [];
            if (record.isOverdue) classes.push('g203-overdue-row');
            if (rowKey(record) === selectedRowKey) classes.push('g203-selected-row');
            return classes.join(' ');
          }}
          onRow={(record) => ({
            onClick: () => setSelectedRowKey(rowKey(record)),
          })}
        />
      </Spin>

      {/* Detail panel under grid */}
      {selectedItem && (
        <div className="g203-detail-panel">
          <label>逾期狀況:</label>
          <span>{selectedItem.agingText}</span>
          <label>幣    別:</label>
          <span>{selectedItem.currency}</span>

          <label>應收金額:</label>
          <span className="detail-value">{fmtAmt(selectedItem.receivableAmt)}</span>
          <label>已收金額:</label>
          <span className="detail-value">{fmtAmt(selectedItem.collectedAmt)}</span>

          <label>未收金額:</label>
          <span className="detail-value">{fmtAmt(selectedItem.outstandingAmt)}</span>
          <label>最後回復日期:</label>
          <span>{fmtDate(selectedItem.dueDate)}</span>

          <label>業務回覆:</label>
          <span className="g203-reply-desc">{replyDesc}</span>
          <span />
          <span />
        </div>
      )}

      {/* Other factors */}
      <div className="g203-other-factors">
        <label data-contract-field="fg03_57">其他因素:</label>
        <TextArea
          rows={3}
          value={selectedItem?.otherFactors ?? ''}
          onChange={(e) => {
            if (selectedItem && selectedRowKey) {
              updateItem(selectedRowKey, 'otherFactors', e.target.value);
            }
          }}
          disabled={!selectedItem || selectedItem.replyCode !== '12'}
          placeholder={selectedItem?.replyCode === '12' ? '請輸入其他因素說明' : ''}
          style={{ maxWidth: 600 }}
        />
      </div>

      {/* Bottom action bar */}
      <div className="g203-action-bar">
        {saving && <span className="g203-saving-overlay">存檔中,請稍後...</span>}
        <Button type="primary" data-acceptance-action="PWG203-ACT-SAVE" onClick={handleSave} loading={saving} disabled={items.length === 0}>
          存檔
        </Button>
          <Button
            data-acceptance-action="PWG203-ACT-GET-PAYMENT-DETAILS"
          onClick={handleShowPaymentDetail}
          disabled={!selectedItem?.hasPaymentDetails}
          style={{ color: selectedItem?.hasPaymentDetails ? '#0000ff' : undefined }}
        >
          收款明細
        </Button>
      </div>

      {/* Payment detail modal */}
      <Modal
        title={`收款沖帳明細 - ${selectedItem?.customerId ?? ''} / ${selectedItem?.deliveryNo ?? ''}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            關閉
          </Button>,
        ]}
        width={700}
      >
        <Spin spinning={detailLoading}>
          <Table<PaymentDetail>
            columns={detailColumns}
            dataSource={paymentDetails}
            rowKey={(r) => `${r.customerId}-${r.deliveryNo}-${r.writeOffDocNo}`}
            size="small"
            pagination={false}
            scroll={{ y: 300 }}
          />
        </Spin>
      </Modal>
      <BatchAcceptanceBridge
        manifest={pwg203f1AcceptanceManifest}
        records={items}
        selectedKeys={selectedItem ? [`${selectedItem.customerId}|${selectedItem.deliveryNo}`] : []}
        keyOf={(item) => `${item.customerId}|${item.deliveryNo}`}
        queryExact={queryAcceptanceExact}
        lastApiResponse={acceptanceLastApiResponse}
        loading={loading || saving}
        onAutoFill={() => { if (items[0]) setSelectedRowKey(rowKey(items[0])); }}
        autoFillDisabled={items.length === 0}
        autoFillReason="請先查詢應收資料；自動填表只會選取第一筆做為範例，不會儲存。"
        screenDraft={{ customerId, salesPersonId, items }}
      />
    </div>
  );
};

export default G203Form;
